import { type FC, useState } from "react";

import { PRIMARY_BUTTON_CLASS_NAME } from "./landingPrimitives";

export interface PublicHeaderNavItem {
  href: string;
  label: string;
}

interface PublicHeaderProps {
  navItems: readonly PublicHeaderNavItem[];
  primaryActionHref: string;
  primaryActionLabel: string;
}

const MOBILE_MENU_ID = "public-mobile-menu";

export const PublicHeaderBrand: FC = () => (
  <a href="/" className="flex min-w-0 items-center gap-2.5">
    <img src="/arti.svg" alt="" className="h-8 w-8" />
    <span className="truncate font-mono text-base font-bold uppercase tracking-[0.2em] text-white">
      Arti
    </span>
  </a>
);

const MenuIcon: FC<{ open: boolean }> = ({ open }) => (
  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d={open ? "m5 5 10 10M15 5 5 15" : "M3.5 6h13M3.5 10h13M3.5 14h13"}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="square"
    />
  </svg>
);

const PublicHeaderActions: FC<{
  menuOpen: boolean;
  onToggleMenu: () => void;
  primaryActionHref: string;
  primaryActionLabel: string;
}> = ({ menuOpen, onToggleMenu, primaryActionHref, primaryActionLabel }) => (
  <div className="ml-3 flex flex-shrink-0 items-center gap-2 sm:gap-4">
    <a
      href="/login"
      className="hidden font-mono text-[11px] font-bold uppercase tracking-widest text-slate-400 transition hover:text-white sm:inline-flex"
    >
      Sign in
    </a>
    <a
      href={primaryActionHref}
      className={`${PRIMARY_BUTTON_CLASS_NAME} whitespace-nowrap !px-4 !py-2.5`}
    >
      {primaryActionLabel}
    </a>
    <button
      type="button"
      aria-controls={MOBILE_MENU_ID}
      aria-expanded={menuOpen}
      aria-label={menuOpen ? "Close menu" : "Open menu"}
      onClick={onToggleMenu}
      className="inline-flex h-9 w-9 cursor-pointer items-center justify-center border-2 border-slate-700 text-slate-300 transition hover:border-emerald-500/60 hover:text-white lg:hidden"
    >
      <MenuIcon open={menuOpen} />
    </button>
  </div>
);

const MobileMenu: FC<{
  navItems: readonly PublicHeaderNavItem[];
  onNavigate: () => void;
}> = ({ navItems, onNavigate }) => (
  <nav
    id={MOBILE_MENU_ID}
    className="mx-auto max-w-7xl border-t-2 border-slate-800 pb-3 lg:hidden"
  >
    <ul className="divide-y divide-slate-800">
      {[...navItems, { href: "/login", label: "Sign in" }].map((item) => (
        <li key={item.href}>
          <a
            href={item.href}
            onClick={onNavigate}
            className="flex items-center justify-between py-3 font-mono text-xs font-bold uppercase tracking-[0.18em] text-slate-300 transition hover:text-emerald-300"
          >
            {item.label}
            <span className="text-slate-600" aria-hidden="true">
              →
            </span>
          </a>
        </li>
      ))}
    </ul>
  </nav>
);

export const PublicHeader: FC<PublicHeaderProps> = ({
  navItems,
  primaryActionHref,
  primaryActionLabel,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <header className="sticky top-0 z-30 border-b-2 border-slate-800 bg-[#0a1020]/85 px-4 backdrop-blur-md sm:px-6">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between">
        <PublicHeaderBrand />
        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="cursor-pointer px-3 py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-400 transition hover:text-emerald-300"
            >
              {item.label}
            </a>
          ))}
        </nav>
        <PublicHeaderActions
          menuOpen={menuOpen}
          onToggleMenu={() => setMenuOpen((open) => !open)}
          primaryActionHref={primaryActionHref}
          primaryActionLabel={primaryActionLabel}
        />
      </div>
      {menuOpen ? (
        <MobileMenu navItems={navItems} onNavigate={() => setMenuOpen(false)} />
      ) : null}
    </header>
  );
};
